import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Grid, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ResetPasswordIcon from '@mui/icons-material/VpnKey';
import ShadowIcon from '@mui/icons-material/Share';
import { DataGrid } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import DefaultApi from '../../Api/DefaultApi';
import TextFieldDialog from '../../Components/TextFieldDialog';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
} from '../../Components/ReactDataGridProps';
import appdata from '../../Services/appdata';

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'name':
    case 'email':
    case 'status':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function Users({ companyId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    status: '',
    enabled: false,
  });
  const [sortColumns, setSortColumns] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    setSelectedRow(null);
    getUsers(companyId);
  }, [companyId]);

  const getUsers = async (comId) => {
    if (comId > -1) {
      DefaultApi.getCompanyUsers(comId)
        .then((res) => {
          if (res.status === 200) {
            setRows(res.data);
          } else {
            toast.error(res.message);
          }
        })
        .catch((error) => {
          toast.error(error.message);
        });
    }
  };

  const handleCellClick = (rowData) => {
    if (selectedRow?.id !== rowData.row.id) {
      setSelectedRow(rowData.row);
    }
  };

  const toggleFilter = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const onClickedDelete = async () => {
    if (!selectedRow) return;
    setSelectedRow(null);
    DefaultApi.deleteUser(companyId, selectedRow.id)
      .then((res) => {
        if (res.status === 200) {
          toast.success(res.message);
          getUsers(companyId);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const onLock = async () => {
    if (!selectedRow) return;
    DefaultApi.lockUser(companyId, selectedRow.id)
      .then((res) => {
        if (res.status === 200) {
          setSelectedRow({
            ...selectedRow,
            status: 'locked',
          });
          getUsers(companyId);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const onUnlock = async () => {
    if (!selectedRow) return;
    DefaultApi.unlockUser(companyId, selectedRow.id)
      .then((res) => {
        if (res.status === 200) {
          setSelectedRow({
            ...selectedRow,
            status: 'active',
          });
          getUsers(companyId);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const onResetPassword = async () => {
    setOpenDialog(true);
  };

  const onEnteredNewPassword = async (newPassword) => {
    if (!selectedRow) return;
    DefaultApi.resetUserPassword(selectedRow.id, newPassword)
      .then((res) => {
        if (res.status === 200) {
          toast.success(res.message);
          getUsers(companyId);
          setOpenDialog(false);
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const onShadowAccount = async () => {
    if (!selectedRow) return;

    DefaultApi.shadowUser(companyId, selectedRow.id)
      .then(res => {
        if (res.status === 200) {
          appdata.setItem('user', res.data.user);
          appdata.setItem('company', res.data.company);
          appdata.setItem('token', res.data.token);
          
          Promise.all([
            DefaultApi.userCompanyOrgId(),
            DefaultApi.getUserRole(),
            DefaultApi.getUserPackage(
              selectedRow.id,
              companyId,
            )
          ])
            .then(([orgIdRes, roleRes, packageRes]) => {
              if (orgIdRes.status === 200) {
                appdata.setItem('company_org_id', orgIdRes.data.company_org_id);
              } else {
                toast.error(orgIdRes.message);
              }
      
              if (roleRes.status === 200) {
                appdata.setItem('role', roleRes.data);
                appdata.setItem('changedrole', {
                  id: roleRes.data.id,
                  pages: roleRes.data.accesses,
                });
                navigate('/');
              } else {
                toast.error(roleRes.message);
              }

              if (packageRes.status === 200) {
                appdata.setItem('package', packageRes.data?.package || null);
              } else {
                toast.error(packageRes.message);
              }
            })
            .catch((error) => toast.error(error.message));
        } else {
          toast.error(res.message);
        }
      })
      .catch(error => toast.error(error.message));
  };

  const tablecolumns = useMemo(() => {
    return [
      {
        key: 'id',
        name: 'ID',
        sortable: true,
        width: 40,
      },
      {
        key: 'name',
        name: 'Name',
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'email',
        name: 'Email',
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.email}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    email: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
      {
        key: 'status',
        name: 'Account Status',
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.status}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    status: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
      },
    ];
    // eslint-disable-next-line
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows?.filter((r) => {
      return (
        (filters.name
          ? r.name?.toLowerCase().includes(filters.name.toLowerCase())
          : true) &&
        (filters.email
          ? r.email?.toLowerCase().includes(filters.email.toLowerCase())
          : true) &&
        (filters.status
          ? r.status?.toLowerCase().includes(filters.status.toLowerCase())
          : true)
      );
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  return (
    <Grid size={5}>
      <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <IconButton aria-label="Search" onClick={toggleFilter}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={onClickedDelete}>
          <DeleteIcon />
        </IconButton>
        {selectedRow &&
          (selectedRow.status === 'inactive' ? (
            <IconButton aria-label="Lock" disabled={true}>
              <LockIcon />
            </IconButton>
          ) : selectedRow.status === 'active' ? (
            <IconButton aria-label="Lock" onClick={onLock}>
              <LockIcon />
            </IconButton>
          ) : (
            <IconButton aria-label="Unlock" onClick={onUnlock}>
              <LockOpenIcon />
            </IconButton>
          ))}
        <IconButton
          aria-label="Reset Password"
          onClick={onResetPassword}
          disabled={!selectedRow}
        >
          <ResetPasswordIcon />
        </IconButton>
        <IconButton
          aria-label="Shadow Account"
          onClick={onShadowAccount}
          disabled={!selectedRow}
        >
          <ShadowIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          columns={tablecolumns}
          rows={sortedRows}
          headerRowHeight={filters.enabled ? 70 : 40}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          onCellClick={handleCellClick}
          style={{ maxHeight: window.innerHeight - 140, resize: 'both' }}
        />
      </FilterContext.Provider>
      <TextFieldDialog
        open={openDialog}
        title="Reset Password"
        content="To reset password, please enter new password"
        label="New password"
        onCancel={() => {
          setOpenDialog(false);
        }}
        onOK={onEnteredNewPassword}
      />
    </Grid>
  );
}

Users.propTypes = {
  companyId: PropTypes.number.isRequired,
};

export default Users;
